import {BASE_URL_V1, BASE_URL, deleteRequest, get, PageResult, post, postForFile} from "./Common";
import { IFreeTextQuestion, ISelectQuestion, ISliderQuestion } from "../constants/types";

export interface BasicNetworkInfo {
  l1Id: number;
  name: string;
}

export interface Portfolio {
  id: string;
  name: string;
  contactUrl: string;
  networks: BasicNetworkInfo[];
}

// Types for portfolio with import settings
export interface ImportSettings {
  id: number;
  l1Id: number;
  portfolioImportSettingsId: number;
  lastRun: number[] | null; // Array format: [year, month, day, hour, minute, second]
  enabled: boolean;
  externalIds: string[];
}

export interface NetworkWithImportSettings {
  l1Id: number;
  name: string;
  importSettings?: ImportSettings;
}

export interface PortfolioWithNetworks {
  id: string;
  name: string;
  contactUrl: string;
  networks: NetworkWithImportSettings[];
}

export interface PortfolioTemplates {
  lifestyleTemplates: PortfolioLifestyleTemplate[];
}

export interface PortfolioLifestyleTemplate {
  id: number;
  name: string;
  questions: {
    freeText: IFreeTextQuestion[];
    select: ISelectQuestion[];
    slider: ISliderQuestion[];
  };
  assignedCount: number;
}

export const LoadAllPortfolios = async (page: number, authToken: string) => {
  const url = `${BASE_URL_V1}/portfolios?page=${page}&size=1000`;

  const results = await get<PageResult<Portfolio>>(url, authToken);

  return results.parsedBody;
};

export const LoadPortfolio = async (portfolioId: string, authToken: string) => {
  const url = `${BASE_URL_V1}/portfolios/${portfolioId}`;

  const results = await get<Portfolio>(url, authToken);

  return results.parsedBody;
};

export const LoadPortfolioWithImportSettings = async (portfolioId: string, authToken: string) => {
  const url = `${BASE_URL_V1}/portfolios/${portfolioId}`;
  const results = await get<PortfolioWithNetworks>(url, authToken);
  return results.parsedBody;
};


export interface PorfolioIntegraion {
  id: number;
  portfolioId: string;
  domain?: string;
  importType: string;
  enabled: boolean;
}

export interface PortfolioIntegrationRequest {
  settings: Array<PorfolioIntegraion>;
}

export interface ApiKeyResponse {
  apiKey: string;
  enabled: boolean;
}


export const LoadPortfolioIntegrations = async (portfolioId: string, authToken: string) => {
  const url = `${BASE_URL_V1}/import/settings/portfolio/${portfolioId}`;

  const results = await get<PortfolioIntegrationRequest>(url, authToken);

  return results.parsedBody;
};

export const GeneratePortfolioApiKey = async (portfolioId: string, authToken: string) => {
  const url = `${BASE_URL_V1}/import/settings/portfolio/${portfolioId}/apiKey/generate`;

  const results = await post<ApiKeyResponse>(url, authToken);

  return results.parsedBody;
};

export interface MigrationField {
  fieldName: string;
  fieldType: string;
}

export interface PossibleMigration {
  origin: string | null;
  target: string;
  fields: MigrationField[];
}

export const GetPossibleMigrations = async (portfolioId: string, l1Id: number, authToken: string) => {
  const url = `${BASE_URL_V1}/import/settings/portfolio/${portfolioId}/migrations/l1Id/${l1Id}`;

  const results = await get<PossibleMigration[]>(url, authToken);

  return results.parsedBody;
};

// New function for network-specific migrations
export const GetNetworkPossibleMigrations = async (l1Id: number, authToken: string) => {
  const url = `${BASE_URL_V1}/import/settings/network/${l1Id}/migrations`;

  const results = await get<PossibleMigration[]>(url, authToken);

  return results.parsedBody;
};

export interface RunMigrationRequest {
  oldPropertyId?: number;
  newPropertyId: number;
  fromType: "ENTRATA" | "SFTP";
  toType: "ENTRATA" | "SFTP";
}

export interface PortfolioNetworkMigrationRequest {
  target: string;
  fields: { [key: string]: number };
}

export const RunMigration = async (l1Id: number, migrationData: RunMigrationRequest, authToken: string) => {
  const url = `${BASE_URL}/integrations/migrate/${l1Id}`;

  const queryParams = new Map<string, string | number | boolean>();
  if (migrationData.oldPropertyId) {
    queryParams.set('oldPropertyId', migrationData.oldPropertyId);
  }
  queryParams.set('newPropertyId', migrationData.newPropertyId);
  queryParams.set('fromType', migrationData.fromType);
  queryParams.set('toType', migrationData.toType);

  const results = await post(url, authToken, queryParams);

  return results;
};

// New function using the migrateNetwork endpoint from import-settings-controller
export const RunNetworkMigration = async (l1Id: number, migrationData: RunMigrationRequest, authToken: string) => {
  const url = `${BASE_URL_V1}/import/settings/network/${l1Id}/migrate`;

  const results = await post(url, authToken, undefined, migrationData);

  return results;
};

// Function using the exact endpoint from import-settings-controller swagger
export const RunPortfolioNetworkMigration = async (portfolioId: string, l1Id: number, migrationData: PortfolioNetworkMigrationRequest, authToken: string) => {
  const url = `${BASE_URL_V1}/import/settings/portfolio/${portfolioId}/migrations/l1Id/${l1Id}`;

  const results = await post(url, authToken, undefined, migrationData);

  return results;
};

// Function for running migration with file upload using multipart/form-data
export const RunPortfolioNetworkMigrationWithFile = async (
  l1Id: number,
  fromType: string,
  toType: string,
  file: File,
  authToken: string
) => {
  const url = `${BASE_URL}/integrations/migrate/${l1Id}?fromType=${encodeURIComponent(fromType)}&toType=${encodeURIComponent(toType)}`;

  const formData = new FormData();
  formData.append("file", file);

  const results = await post(url, authToken, undefined, formData);

  return results;
};

export const UpdateNetworkImportSettings = async (portfolioId: string, l1Id: number, networkSettingsId: number, enabled: boolean, authToken: string) => {
  const url = `${BASE_URL_V1}/import/settings/portfolio/${portfolioId}/l1Id/${l1Id}/networkSettings/${networkSettingsId}`;

  const results = await post(url, authToken, undefined, { enabled });

  return results;
};

export const UpdatePortfolioIntegration = async (portfolioId: string, integrationId: number, enabled: boolean, importType: string, authToken: string) => {
  const url = `${BASE_URL_V1}/import/settings/portfolio/${portfolioId}/importType/${importType}/${integrationId}`;

  const results = await post(url, authToken, undefined, { enabled });

  return results;
};


interface PortfolioReportRequest {
  portfolioId: string;
  reportType: "ALL_ACTIVE" | "DATE_RANGE";
  // LocalDate startDate, LocalDate endDate, PortfolioReportRequestOptions options
}

export enum MatchingRoundStatus {
  "UPCOMING"= "UPCOMING",
  "ACTIVE" = "ACTIVE",
  "MATCHING" = "MATCHING",
  "CLOSED" = "CLOSED",
  "ARCHIVED" = "ARCHIVED"
}

interface PortfolioUserReportRequest {
  matchingRoundStatuses?: MatchingRoundStatus[];
  subnetwork?: Number,
  unitType?: Number,
  userStatus?: Number,
  registeredStatus?: Number,
  includeQuickJoinInfo?: Boolean,
  includeRegistrationInfo?: Boolean,
  includeSocialAccountsInfo?: Boolean,
  includeRoommatesInfo?: Boolean,
  // LocalDate startDate, LocalDate endDate, PortfolioReportRequestOptions options
}

export const DownLoadPortfolioReport = async (portfolioId: string, request: PortfolioReportRequest, authToken: string) => {
  const url = `${BASE_URL_V1}/portfolios/${portfolioId}/report.csv`;
  return await postForFile(url, request, authToken);
};

export const DownLoadPortfolioUserReport = async (portfolioId: string, request: PortfolioUserReportRequest, authToken: string) => {
  const url = `${BASE_URL_V1}/portfolios/${portfolioId}/user_report.csv`;
  return await post(url,authToken, undefined, request);
};

export const CreatePortfolio = async (name: string, contactUrl: string, authToken: string) => {
  const url = `${BASE_URL_V1}/portfolios`;

  const results = await post<Portfolio>(url, authToken, undefined, { "name": name, "contactUrl": contactUrl });

  return results.parsedBody;
};

export const GetLifestylePortfolioTemplates = async (portfolioId: string, authToken: string) => {
  const url = `${BASE_URL_V1}/portfolios/${portfolioId}/templates/lifestyle`;

  const results = await get<PortfolioTemplates>(url, authToken);

  return results.parsedBody;
};

export const CreatePortfolioLifestyleTemplate = async (templateName: string, portfolioId: string, authToken: string) => {
  const url = `${BASE_URL_V1}/portfolios/${portfolioId}/templates/lifestyle`;

  const results = await post<PortfolioLifestyleTemplate>(url, authToken, undefined, {
    "portfolioId": portfolioId,
    "templateName": templateName
  });

  return results.parsedBody;
};

export const GetPortfolioLifestyleTemplate = async (templateId: string, portfolioId: string, authToken: string) => {
  const url = `${BASE_URL_V1}/portfolios/${portfolioId}/templates/lifestyle/${templateId}`;

  const results = await get<PortfolioLifestyleTemplate>(url, authToken);

  return results.parsedBody;
};

export enum QuestionType {
  SLIDER,
  SELECT,
  FREE_TEXT
}

export interface PortfolioTemplateQuestionAnswer {
  answerOrder: number;
  answerText: string;
  answerId?: number;
}

export interface PortfolioTemplateQuestionCreationRequest {
  portfolioId: string;
  templateId: number;
  questionType: QuestionType;
  questionText: String;
  retainAnswer: boolean;
  disclaimer?: String;
  placeholder?: String;
  numberOfSliderOptions?: number;
  answers?: PortfolioTemplateQuestionAnswer[];
  tagId?: number;
}

export interface PortfolioTemplateQuestionUpdateRequest {
  questionId: number;
  portfolioId: string;
  templateId: number;
  questionType: QuestionType;
  retainAnswer: boolean;
  questionText: String;
  disclaimer?: String;
  placeholder?: String;
  numberOfSliderOptions?: number;
  answers?: PortfolioTemplateQuestionAnswer[];
  tagId?: number;
}

export const CreatePortfolioTemplateQuestion = async (templateId: number, portfolioId: string, authToken: string, body: PortfolioTemplateQuestionCreationRequest) => {
  const url = `${BASE_URL_V1}/portfolios/${portfolioId}/templates/lifestyle/${templateId}/question`;

  const results = await post(url, authToken, undefined, body);

  return results.parsedBody;
};

export const UpdatePortfolioTemplateQuestion = async (questionId: number, templateId: number, portfolioId: string, authToken: string, body: PortfolioTemplateQuestionUpdateRequest) => {
  const url = `${BASE_URL_V1}/portfolios/${portfolioId}/templates/lifestyle/${templateId}/question/${questionId}`;

  const results = await post(url, authToken, undefined, body);

  return results.parsedBody;
};

export const DeletePortfolioTemplateQuestion = async (templateId: number, portfolioId: string, questionId: number, authToken: string) => {
  const url = `${BASE_URL_V1}/portfolios/${portfolioId}/templates/lifestyle/${templateId}/question/${questionId}`;

  const results = await deleteRequest(url, authToken, undefined);

  return results.parsedBody;
};

export const ReOrderPortfolioTemplateQuestions = async (templateId: number, portfolioId: string, questionType: QuestionType, questionOrder: [number], authToken: string) => {
  const url = `${BASE_URL_V1}/portfolios/${portfolioId}/templates/lifestyle/${templateId}/question/order`;

  const body = {
    portfolioId: portfolioId,
    templateId: templateId,
    questionType: questionType,
    questionOrder: questionOrder
  };

  const results = await post(url, authToken, undefined, body);

  return results.parsedBody;
};


export const AssignTemplateToL3 = async (l1Id: string, templateId: number, portfolioId: string, units: { l1Id: string, l2Id: string, l3Id: string }[], authToken: string) => {
  const url = `${BASE_URL}/networks/${l1Id}/template/lifestyle`;

  const body = {
    portfolioId,
    templateId,
    units
  };

  const results = await post(url, authToken, undefined, body);

  return results.parsedBody;
};

export const RemoveTemplateFromL3 = async (l1Id: string, l2Id: string, l3Id: string, authToken: string) => {
  const url = `${BASE_URL}/networks/${l1Id}/template/lifestyle`;

  const results = await deleteRequest(url, authToken, undefined, {"l1Id": l1Id, "l2Id": l2Id, "l3Id": l3Id});

  return results.parsedBody;
};

// Email Whitelist API functions

export interface EmailWhitelistEntry {
  id: number;
  l1Id: number;
  email: string;
}

export const GetEmailWhitelist = async (l1Id: number, authToken: string) => {
  const url = `${BASE_URL}/integrations/email/import/${l1Id}/whitelist`;

  const results = await get<EmailWhitelistEntry[]>(url, authToken);

  return results.parsedBody;
};

export const AddEmailWhitelist = async (l1Id: number, email: string, authToken: string) => {
  const url = `${BASE_URL}/integrations/email/import/${l1Id}/whitelist`;

  const results = await post(url, authToken, undefined, { email });

  return results;
};

export const EditEmailWhitelist = async (l1Id: number, emailId: number, email: string, authToken: string) => {
  const url = `${BASE_URL}/integrations/email/import/${l1Id}/whitelist/${emailId}`;

  const results = await post(url, authToken, undefined, { email });

  return results;
};

export const RemoveEmailWhitelist = async (l1Id: number, whitelistId: number, authToken: string) => {
  const url = `${BASE_URL}/integrations/email/import/${l1Id}/whitelist/${whitelistId}`;

  const results = await deleteRequest(url, authToken);

  return results;
};
